import React, { useMemo, useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { LottiePlayer } from '@shared/components';
import { useAuth } from '@shared/components/AuthProvider';
import { JoyLogo } from '@shared/createEvent/JoyLogo';
import { getTruncatedEmail } from '@shared/createEvent/utils/getTruncatedEmail';
import { BoxProps, Divider, Flex, LinkV2, SpacingStack, TextV2 } from '@withjoy/joykit';

import { EventDetailsWrapper, StyledLinkV2, LottieWrapper, styles } from './EventDetailsContainer.styles';

export interface EventDetailsContainerProps extends BoxProps {
  topBanner?: JSX.Element;
  lottie?: {
    play: boolean;
    lottieUrl: string;
  };
  header: {
    title: string;
    subTitle: string;
    icon?: JSX.Element;
    showLogo?: boolean;
  };
}

export const EventDetailsContainer: React.FC<EventDetailsContainerProps> = ({ topBanner, lottie, header, paddingTop = 24 + 56, paddingBottom = 9, children }) => {
  const authProvider = useAuth();
  const { isLoggedIn } = useAuth();
  const { t } = useTranslation('createEvent');

  const { changeAccount, signOut } = t('notYou');

  const { showLogo, title, subTitle, icon } = header;

  const email: string = useMemo(() => getTruncatedEmail(authProvider.currentUser.profile?.email ?? ''), [authProvider.currentUser.profile]);

  const handleSignOut = useCallback(() => authProvider.provideLogout({}, `${window.location.href}`), [authProvider]);
  const goToSignIn = useCallback(() => authProvider.loginManager.authService.authorize(window.location.href), [authProvider.loginManager.authService]);

  return (
    <>
      {lottie && (
        <LottieWrapper>
          <LottiePlayer {...lottie} />
        </LottieWrapper>
      )}
      <EventDetailsWrapper paddingX={6} paddingTop={paddingTop} paddingBottom={paddingBottom} spacing={3} width="100%" alignItems="center">
        {topBanner}
        {showLogo && <JoyLogo />}
        <SpacingStack spacing={9}>
          <SpacingStack spacing={icon ? 3 : 6} alignItems="center">
            <TextV2 textAlign="center" as="h1" typographyVariant="Hed40" fontFamily="'Austin News Deck Web'">
              {title}
            </TextV2>
            {icon && (
              <Flex style={{ gap: 8 }} alignItems="center">
                <Divider color="mono5" width={64} />
                {icon}
                <Divider color="mono5" width={64} />
              </Flex>
            )}
            {!icon && <Divider color="mono5" width={80} />}
            <TextV2 textAlign="center" typographyVariant="body3" color="mono14" fontSize={18} marginX="auto" maxWidth={400}>
              {subTitle}{' '}
              {!isLoggedIn && (
                <LinkV2 onClick={goToSignIn} textDecoration="none">
                  <TextV2 as="span" typographyVariant="body3" color="linkText" fontSize={18}>
                    {t('createAccount', 'signIn')()}
                  </TextV2>
                </LinkV2>
              )}
            </TextV2>
          </SpacingStack>
          <SpacingStack spacing={8}>
            {children}
            {isLoggedIn && (
              <TextV2 textAlign="center" typographyVariant="body1" color="mono12">
                {changeAccount({ email })}{' '}
                <StyledLinkV2 onClick={handleSignOut} {...styles.signOutLinkStyles} typographyVariant="button1">
                  {signOut()}
                </StyledLinkV2>
              </TextV2>
            )}
          </SpacingStack>
        </SpacingStack>
      </EventDetailsWrapper>
    </>
  );
};
